import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";

/**
 * Final grade: film grain + vignette + edge chromatic aberration
 * in a single fullscreen pass.
 */
const GradeShader = {
  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uGrain: { value: 0.03 },
    uVignette: { value: 0.5 },
    uAberration: { value: 0.0004 }
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    precision highp float;

    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uGrain;
    uniform float uVignette;
    uniform float uAberration;

    varying vec2 vUv;

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
    }

    void main() {
      vec2 centered = vUv - 0.5;
      float r2 = dot(centered, centered);

      // Chromatic aberration grows toward the edges.
      vec2 shift = centered * r2 * uAberration * 60.0;
      float cr = texture2D(tDiffuse, vUv + shift).r;
      vec2 gb = vec2(
        texture2D(tDiffuse, vUv).g,
        texture2D(tDiffuse, vUv - shift).b
      );
      vec3 col = vec3(cr, gb.x, gb.y);

      // Film grain (animated).
      float grain = hash(vUv * vec2(1920.0, 1080.0) + fract(uTime) * 43.0) - 0.5;
      col += grain * uGrain;

      // Vignette.
      float vig = smoothstep(0.9, 0.25, r2 * (1.0 + uVignette));
      col *= mix(1.0, vig, uVignette);

      gl_FragColor = vec4(col, 1.0);
    }
  `
};

/**
 * @returns {{ composer: EffectComposer, update:(t:number)=>void, resize:(w:number,h:number)=>void }}
 */
export function createPostFX({ renderer, scene, camera, tier }) {
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  let bloom = null;
  if (tier.bloom) {
    bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      /* strength */ tier.name === "mobile" ? 0.5 : 0.65,
      /* radius */ 0.35,
      /* threshold */ 0.72
    );
    composer.addPass(bloom);
  }

  let grade = null;
  if (tier.filmPass) {
    grade = new ShaderPass(GradeShader);
    composer.addPass(grade);
  }

  return {
    composer,
    update(t) {
      if (grade) grade.uniforms.uTime.value = t;
    },
    resize(w, h) {
      composer.setSize(w, h);
      if (bloom) {
        // Bloom is low-frequency; running its mip chain at half device
        // resolution is visually identical and much cheaper.
        const pr = renderer.getPixelRatio();
        bloom.setSize(Math.round(w * pr * 0.5), Math.round(h * pr * 0.5));
      }
    }
  };
}
